import styled from '@emotion/styled';
import { useEffect, useState } from 'react';

import {
  Audio,
  AuthContainer,
  Button,
  Logo,
  PhoneField,
  PhoneFieldResult,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { tempLSPhoneKey, validatePhoneNumber } from '@taleemabad/shared';

interface CnicProps {
  logo: string;
  onSubmitted: (data: PhoneFieldResult) => void;
}
export const CnicScreen = ({ logo, onSubmitted }: CnicProps) => {
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formData, setFormData] = useState<PhoneFieldResult>({
    hasError: !localStorage.getItem(tempLSPhoneKey),
    value: localStorage.getItem(tempLSPhoneKey) ?? '03',
    name: 'phone',
  });

  const handleSubmit = async () => {
    setHasSubmitted(true);
    onSubmitted(formData);
  };
  useEffect(() => {
    const phoneField = document.getElementById('phone') as HTMLInputElement;
    if (phoneField) {
      phoneField.focus();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [formData]);

  return (
    <AuthContainer>
      <StyledImageWrapper>
        <Logo
          altName="outer-layer"
          width={275}
          src={`../../assets/${logo}`}
          data-cy="sign-in-app-logo"
        />
      </StyledImageWrapper>

      <StyledDiv flexDirection="column" marginBottom={42}>
        <Typography
          variant="textHeadlineLargeSurface"
          data-cy="sign-in-get-started"
        >
          Enter your CNIC
        </Typography>
        <StyledDiv
          flexDirection="column"
          justifyContent="center"
          alignItem="flex-start"
          width="100%"
          marginBottom={12}
          marginTop={12}
        >
          <Audio
            isPlaying={true}
            src={
              'https://ai-textract-bucket.s3.ap-south-1.amazonaws.com/audio-files/Enter+phone+number.mp3'
            }
          />
        </StyledDiv>
        <Spacing marginBottom="xxl">
          <Typography
            variant="textBodyLargeSurface"
            data-cy="sign-in-phone-number-text"
          >
            Please follow the format XXXXX-XXXXXXX-X
          </Typography>
        </Spacing>
        <PhoneField
          name="phone"
          type="tel"
          variant="outlined"
          label="CNIC"
          placeholder="CNIC Number"
          value={formData.value}
          autocomplete="username"
          id="phone"
          data-cy="phone-number-field"
          errorText={hasSubmitted ? errorMessage : ''}
          onChange={(data: PhoneFieldResult): void => {
            data.hasError = !!validatePhoneNumber(data.value);
            setErrorMessage(validatePhoneNumber(data.value));
            setFormData(data);
          }}
        />
      </StyledDiv>
      <Button
        children="Next"
        type="submit"
        onClick={handleSubmit}
        data-cy="begin-button"
      />
    </AuthContainer>
  );
};

const StyledImageWrapper = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  margin-bottom: 68px;
  ${mq(BreakpointSizes.xs)} {
    display: flex;
  }
`;
